/**
 * @voltxampere/engine — Transient MNA assembly
 *
 * Mirror of `dc/mna-builder.ts`, with two differences:
 *
 *  1. Stamp dispatch goes through the transient registry
 *     (`getTransientStampOrThrow`) so capacitor / inductor pick up
 *     their integration companion instead of the DC no-op / 0-V
 *     short.
 *  2. The stamp context carries `h`, `method`, `xPrevPrev`,
 *     `firstStep`, and `previousCurrent` — the per-step state the
 *     companion stamps need to reconstruct the SPECS §3.8 Geq/Ieq.
 *
 * The DC builder's gmin / sourceScale knobs are exposed too — the
 * transient solver uses them sparingly (gmin = 0 by default; only
 * raised if Newton on a stiff transient step starts struggling).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { createSparse } from "@voltxampere/math";
import { getTransientStampOrThrow } from "../stamps/register.js";
import { createTransientStampContext } from "../stamps/transient/context.js";
export function buildTransientMNA(circuit, plan, opts) {
    const { size, nodeCount, internalNodeCount } = plan;
    const gmin = opts.gmin ?? 0;
    const sourceScale = opts.sourceScale ?? 1;
    if (gmin < 0 || !Number.isFinite(gmin)) {
        throw new ContractViolationError(`buildTransientMNA: gmin must be a non-negative finite number, got ${String(gmin)}`);
    }
    if (!Number.isFinite(sourceScale)) {
        throw new ContractViolationError(`buildTransientMNA: sourceScale must be finite, got ${String(sourceScale)}`);
    }
    if (opts.xPrev.length !== size) {
        throw new ContractViolationError(`buildTransientMNA: xPrev length ${String(opts.xPrev.length)} ≠ N+I+M ${String(size)}`);
    }
    const triplets = [];
    const rhs = new Float64Array(size);
    const ctx = createTransientStampContext({
        nodeCount,
        internalNodeCount,
        internalNodeIndices: plan.internalNodeIndices,
        vSourceCount: plan.vSourceCount,
        netToIndex: plan.netToIndex,
        groundNetId: plan.groundNetId,
        vSourceIdToIndex: plan.vSourceIdToIndex,
        triplets,
        rhs,
        xPrev: opts.xPrev,
        t: opts.t,
        gmin,
        sourceScale,
        junctionHistory: opts.junctionHistory ?? new Map(),
        enableDeltaVDamping: opts.enableDeltaVDamping ?? true,
        ...(opts.temperature !== undefined ? { temperature: opts.temperature } : {}),
        h: opts.h,
        hPrev: opts.hPrev,
        method: opts.method,
        firstStep: opts.firstStep,
        xStepPrev: opts.xStepPrev,
        xStepPrevPrev: opts.xStepPrevPrev,
        previousCurrent: opts.previousCurrent,
    });
    for (const c of circuit.components) {
        const stamp = getTransientStampOrThrow(c.type);
        stamp(ctx, c);
    }
    if (gmin > 0) {
        const kclEnd = nodeCount + internalNodeCount;
        for (let i = 0; i < kclEnd; i++) {
            triplets.push({ row: i, col: i, value: gmin });
        }
    }
    const Y = createSparse(size, size, triplets);
    return { Y, rhs, triplets };
}
//# sourceMappingURL=mna-builder.js.map